import { Fragment } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

/**
 * The island north to south, municipalities with their named communities
 * nested underneath.
 *
 * This is navigation and brand at once: eighteen miles of one road is the fact
 * about LBI that a town dropdown cannot carry, and the nesting is the
 * `town_within` parent/child model made visible. Every node writes
 * ?town_within=, which matches the parent AND its children -- a plain town_id
 * filter on Long Beach Township returns almost nothing.
 *
 * businesses_count is each town's own published rows, so a parent's number does
 * not include the communities listed under it. That is why the child nodes show
 * their own counts instead of the parent showing a total nobody can reconcile.
 */
function Node({ town, active, child = false }) {
  const count = town.businesses_count;

  return (
    <li className={child ? 'rail-node rail-child' : 'rail-node'}>
      <Link
        to={`/directory?town_within=${town.slug}`}
        className={active ? 'rail-link rail-on' : 'rail-link'}
        aria-current={active ? 'page' : undefined}
      >
        <span className="rail-dot" aria-hidden="true" />
        <span>{town.name}</span>
        {count !== undefined && <span className="rail-count">{count}</span>}
      </Link>
    </li>
  );
}

export default function IslandRail({ towns }) {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  // Nothing is filtered anywhere but the directory, so nothing is current.
  const selected = pathname.startsWith('/directory') ? searchParams.get('town_within') : null;

  if (!towns?.length) return null;

  return (
    <nav className="rail" aria-label="Towns, north to south">
      <span className="eyebrow rail-head">The island &middot; N &rarr; S</span>
      <ul className="rail-list">
        {towns.map((town) => (
          <Fragment key={town.id}>
            <Node town={town} active={selected === town.slug} />
            {town.communities?.map((community) => (
              <Node key={community.id} town={community} active={selected === community.slug} child />
            ))}
          </Fragment>
        ))}
      </ul>
    </nav>
  );
}
